import { ExpressionError } from './ast';
import { IDENTIFIER_PATTERN } from './names';

export type TokenType =
  'number' | 'identifier' | 'variable' | 'operator' | 'lparen' | 'rparen' | 'comma' | 'end';

export interface Token {
  readonly type: TokenType;
  readonly text: string;
  readonly position: number;
}

const OPERATORS = new Set(['+', '-', '*', '/', '%', '^']);
const IDENTIFIER = new RegExp(`^${IDENTIFIER_PATTERN}`);

export function tokenize(source: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;

  while (i < source.length) {
    const ch = source[i] as string;

    if (/\s/.test(ch)) {
      i += 1;
      continue;
    }

    if (/[0-9.]/.test(ch)) {
      const match = /^[0-9]*\.?[0-9]+(?:[eE][+-]?[0-9]+)?/.exec(source.slice(i));
      if (!match) throw new ExpressionError(`Invalid number at ${i}`, i);
      tokens.push({ type: 'number', text: match[0], position: i });
      i += match[0].length;
      continue;
    }

    if (/[A-Za-z_]/.test(ch)) {
      const match = IDENTIFIER.exec(source.slice(i)) as RegExpExecArray;
      tokens.push({ type: 'identifier', text: match[0], position: i });
      i += match[0].length;
      continue;
    }

    if (ch === '$') {
      const match = IDENTIFIER.exec(source.slice(i + 1));
      if (!match) throw new ExpressionError(`Expected a variable name after "$" at ${i}`, i);
      tokens.push({ type: 'variable', text: `$${match[0]}`, position: i });
      i += match[0].length + 1;
      continue;
    }

    if (OPERATORS.has(ch)) {
      tokens.push({ type: 'operator', text: ch, position: i });
      i += 1;
      continue;
    }

    if (ch === '(' || ch === ')' || ch === ',') {
      const type = ch === '(' ? 'lparen' : ch === ')' ? 'rparen' : 'comma';
      tokens.push({ type, text: ch, position: i });
      i += 1;
      continue;
    }

    throw new ExpressionError(`Unexpected character "${ch}"`, i);
  }

  tokens.push({ type: 'end', text: '', position: source.length });
  return tokens;
}
